import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Button,
  Input,
  Dropdown,
  Option,
  Tooltip,
  MessageBar,
  MessageBarBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  ProgressBar,
} from '@fluentui/react-components';
import {
  Eye20Regular,
  Search20Regular,
  Dismiss20Regular,
  HatGraduation20Regular,
  PersonBoard20Regular,
  ArrowSync20Regular,
  CheckmarkCircle20Filled,
  ErrorCircle20Regular,
} from '@fluentui/react-icons';
import type { EducationUser, EducationUserSummary, PagedResponse, FixGroupsOperationResponse, CheckBackupResponse } from '../../types';
import educationService from '../../services/educationService';
import ExportButton from '../Common/ExportButton';
import UserDetails from './UserDetails';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 200px)',
  },
  searchArea: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  searchDropdown: {
    minWidth: '150px',
  },
  searchInput: {
    flex: 1,
    minWidth: '300px',
    maxWidth: '500px',
  },
  totalText: {
    marginLeft: 'auto',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  table: {
    backgroundColor: tokens.colorNeutralBackground1,
    width: '100%',
    tableLayout: 'fixed',
  },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  headerCell: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  headerCellCenter: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  iconCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  studentIcon: {
    color: tokens.colorPaletteBlueForeground2,
  },
  teacherIcon: {
    color: tokens.colorPaletteGreenForeground2,
  },
  otherIcon: {
    color: tokens.colorNeutralForeground3,
  },
  statusActive: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusInactive: {
    color: tokens.colorNeutralForeground4,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  paginationInfo: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  paginationButtons: {
    display: 'flex',
    gap: '8px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  loadingMore: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
    gap: '8px',
  },
  warningBar: {
    marginBottom: '12px',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
    color: tokens.colorNeutralForeground3,
  },
  fixGroupsCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2px',
    width: '100%',
  },
  fixGroupsProgress: {
    width: '100%',
    minWidth: '60px',
  },
  fixGroupsText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  fixGroupsSuccess: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  fixGroupsError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    cursor: 'pointer',
  },
  dialogSurface: {
    maxWidth: '450px',
  },
});

type SearchBy = 'name' | 'upn' | 'teacherId' | 'studentId' | 'id';

const PAGE_SIZE = 25;

export const UsersList: React.FC = () => {
  const styles = useStyles();

  // Data state
  const [users, setUsers] = useState<EducationUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [nextLink, setNextLink] = useState<string | null>(null);

  // Search state
  const [searchBy, setSearchBy] = useState<SearchBy>('name');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Summary state (class counts)
  const [userSummaries, setUserSummaries] = useState<Map<string, EducationUserSummary>>(new Map());
  const [loadingSummaries, setLoadingSummaries] = useState<Set<string>>(new Set());

  // Fix Groups state
  const [fixGroupsOperations, setFixGroupsOperations] = useState<Map<string, FixGroupsOperationResponse>>(new Map());
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupInfo, setBackupInfo] = useState<{ userId: string; userDisplayName: string; backup: CheckBackupResponse } | null>(null);
  const [checkingBackup, setCheckingBackup] = useState<Set<string>>(new Set());
  const pollingIntervals = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const loadUsers = async (isNewSearch: boolean = true) => {
    if (!searchTerm.trim()) {
      setError('Por favor, insira um termo de busca');
      return;
    }

    try {
      if (isNewSearch) {
        setLoading(true);
        setUsers([]);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      setHasSearched(true);

      // Handle specific ID searches (studentId, teacherId) differently
      if (isNewSearch && (searchBy === 'studentId' || searchBy === 'teacherId')) {
        let user: EducationUser | null = null;

        if (searchBy === 'studentId') {
          user = await educationService.getUserByStudentExternalId(searchTerm);
        } else if (searchBy === 'teacherId') {
          user = await educationService.getUserByTeacherExternalId(searchTerm);
        }

        if (user) {
          setUsers([user]);
        } else {
          setUsers([]);
        }
        setNextLink(null);
        return;
      }

      let response: PagedResponse<EducationUser>;

      if (isNewSearch) {
        // First page - use regular getUsers
        response = await educationService.getUsers(1, PAGE_SIZE, searchTerm, undefined);
        setUsers(response.data);
      } else if (nextLink) {
        // Subsequent pages - use nextLink endpoint
        response = await educationService.getUsersWithNextLink(nextLink);
        setUsers(prev => [...prev, ...response.data]);
      } else {
        return;
      }

      setNextLink(response.nextLink || null);
    } catch (err: any) {
      console.error('Error loading users:', err);
      setError(err.message || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    loadUsers(true);
  };

  const handleLoadMore = () => {
    loadUsers(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchBy('name');
    setUsers([]);
    setHasSearched(false);
    setError(null);
    setNextLink(null);
    setUserSummaries(new Map());
    setLoadingSummaries(new Set());
  };

  // Load user summaries (class counts)
  const loadUserSummaries = useCallback(async (userIds: string[]) => {
    // Only load for users not already loaded or loading
    const idsToLoad = userIds.filter(id =>
      !userSummaries.has(id) && !loadingSummaries.has(id)
    );

    if (idsToLoad.length === 0) return;

    // Mark as loading
    setLoadingSummaries(prev => new Set([...prev, ...idsToLoad]));

    try {
      const summaries = await educationService.getUsersSummary(idsToLoad);

      setUserSummaries(prev => {
        const newMap = new Map(prev);
        summaries.forEach(s => newMap.set(s.userId, s));
        return newMap;
      });
    } catch (err) {
      console.error('Error loading user summaries:', err);
    } finally {
      setLoadingSummaries(prev => {
        const newSet = new Set(prev);
        idsToLoad.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [userSummaries, loadingSummaries]);

  // Load summaries when users change
  useEffect(() => {
    if (users.length > 0) {
      loadUserSummaries(users.map(u => u.id));
    }
  }, [users, loadUserSummaries]);

  const handleViewUser = (userId: string) => {
    setSelectedUserId(userId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUserId(null);
  };

  // Fix Groups handlers
  const startPolling = useCallback((operationId: string, userId: string) => {
    // Clear existing interval if any
    const existingInterval = pollingIntervals.current.get(userId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const operation = await educationService.getFixGroupsOperation(operationId);
        setFixGroupsOperations(prev => {
          const newMap = new Map(prev);
          newMap.set(userId, operation);
          return newMap;
        });

        // Stop polling when operation is complete
        if (operation.status === 'Completed' || operation.status === 'Failed') {
          clearInterval(interval);
          pollingIntervals.current.delete(userId);
        }
      } catch (err) {
        console.error('Error polling fix groups operation:', err);
        clearInterval(interval);
        pollingIntervals.current.delete(userId);
      }
    }, 2000);

    pollingIntervals.current.set(userId, interval);
  }, []);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      pollingIntervals.current.forEach((interval) => clearInterval(interval));
    };
  }, []);

  const handleFixGroupsClick = async (user: EducationUser) => {
    try {
      setCheckingBackup(prev => new Set(prev).add(user.id));

      // Check if backup exists
      const backup = await educationService.checkFixGroupsBackup(user.id);

      if (backup.hasBackup) {
        // Show dialog to choose
        setBackupInfo({
          userId: user.id,
          userDisplayName: user.displayName,
          backup,
        });
        setBackupDialogOpen(true);
      } else {
        // Start fix groups directly (online mode)
        await startFixGroups(user.id, user.displayName, false);
      }
    } catch (err: any) {
      console.error('Error checking backup:', err);
      setError(err.message || 'Erro ao verificar backup');
    } finally {
      setCheckingBackup(prev => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };

  const startFixGroups = async (userId: string, userDisplayName: string, useBackup: boolean) => {
    try {
      const operation = await educationService.startFixGroups(userId, userDisplayName, useBackup);

      setFixGroupsOperations(prev => {
        const newMap = new Map(prev);
        newMap.set(userId, operation);
        return newMap;
      });

      // Start polling for updates
      startPolling(operation.id, userId);
    } catch (err: any) {
      console.error('Error starting fix groups:', err);
      if (err.response?.status === 409) {
        setError('Ja existe uma operacao em andamento para este usuario');
      } else {
        setError(err.message || 'Erro ao iniciar correcao de grupos');
      }
    }
  };

  const handleBackupChoice = async (useBackup: boolean) => {
    if (!backupInfo) return;

    setBackupDialogOpen(false);
    await startFixGroups(backupInfo.userId, backupInfo.userDisplayName, useBackup);
    setBackupInfo(null);
  };

  const renderFixGroupsCell = (user: EducationUser) => {
    const operation = fixGroupsOperations.get(user.id);
    const isChecking = checkingBackup.has(user.id);

    // Loading state while checking backup
    if (isChecking) {
      return (
        <div className={styles.fixGroupsCell}>
          <Spinner size="tiny" />
        </div>
      );
    }

    // No operation - show button
    if (!operation) {
      return (
        <Tooltip content="Corrigir grupos do usuario" relationship="label">
          <Button
            appearance="subtle"
            icon={<ArrowSync20Regular />}
            size="small"
            onClick={() => handleFixGroupsClick(user)}
          />
        </Tooltip>
      );
    }

    // Operation in progress
    if (operation.status === 'Pending' || operation.status === 'Running') {
      return (
        <div className={styles.fixGroupsCell}>
          <ProgressBar
            value={operation.progressPercentage / 100}
            className={styles.fixGroupsProgress}
          />
          <Text className={styles.fixGroupsText}>
            {operation.processedGroups}/{operation.totalGroups}
          </Text>
        </div>
      );
    }

    // Completed successfully
    if (operation.status === 'Completed' && operation.failedGroups === 0) {
      return (
        <Tooltip content={`Concluido em ${operation.durationFormatted}`} relationship="label">
          <span className={styles.fixGroupsSuccess}>
            <CheckmarkCircle20Filled /> {operation.totalGroups}
          </span>
        </Tooltip>
      );
    }

    // Completed with errors or failed
    const errorTooltip = operation.errors.length > 0
      ? operation.errors.slice(0, 5).map(e =>
          `${e.groupName}: ${e.errorMessage}${e.wasRemoved ? ' (usuario FORA do grupo!)' : ''}`
        ).join('\n')
      : operation.fatalError || 'Erro desconhecido';

    return (
      <Tooltip
        content={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{errorTooltip}</pre>}
        relationship="label"
      >
        <span className={styles.fixGroupsError}>
          <ErrorCircle20Regular /> {operation.failedGroups}
        </span>
      </Tooltip>
    );
  };

  // Helper to render count cell
  const renderCountCell = (userId: string, field: 'studentClassCount' | 'teacherClassCount') => {
    if (loadingSummaries.has(userId)) {
      return <Spinner size="tiny" />;
    }
    const summary = userSummaries.get(userId);
    if (!summary) return '-';
    const value = summary[field];
    if (value === -1) return <span className={styles.errorText}>Erro</span>;
    return value;
  };

  const getExportData = () => {
    return users.map(user => {
      const summary = userSummaries.get(user.id);
      return {
        displayName: user.displayName,
        userPrincipalName: user.userPrincipalName,
        mail: user.mail,
        primaryRole: user.primaryRole,
        externalId: user.student?.externalId || user.teacher?.externalId || '',
        studentClassCount: summary?.studentClassCount ?? '',
        teacherClassCount: summary?.teacherClassCount ?? '',
      };
    });
  };

  const exportColumns = [
    { key: 'displayName', label: 'Nome' },
    { key: 'externalId', label: 'ID Externo' },
    { key: 'studentClassCount', label: 'Aluno' },
    { key: 'teacherClassCount', label: 'Professor' },
  ];

  // Calculate display range
  const startItem = users.length > 0 ? 1 : 0;
  const endItem = users.length;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Carregando usuários..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && (
        <MessageBar intent="error" className={styles.warningBar}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* SEARCH AREA */}
      <div className={styles.searchArea}>
        <Dropdown
          className={styles.searchDropdown}
          value={searchBy === 'name' ? 'Nome' : searchBy === 'upn' ? 'UPN' : searchBy === 'teacherId' ? 'ID Professor' : searchBy === 'studentId' ? 'ID Aluno' : 'ID Microsoft'}
          selectedOptions={[searchBy]}
          onOptionSelect={(_, data) => setSearchBy(data.optionValue as SearchBy)}
        >
          <Option value="name">Nome</Option>
          <Option value="upn">UPN</Option>
          <Option value="teacherId">ID Professor</Option>
          <Option value="studentId">ID Aluno</Option>
          <Option value="id">ID Microsoft</Option>
        </Dropdown>

        <Input
          className={styles.searchInput}
          placeholder="Digite o termo de busca..."
          value={searchTerm}
          onChange={(_, data) => setSearchTerm(data.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />

        <Button
          appearance="primary"
          icon={<Search20Regular />}
          onClick={handleSearch}
        >
          Buscar
        </Button>

        {hasSearched && (
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            onClick={handleClearSearch}
          >
            Limpar
          </Button>
        )}

        <ExportButton
          data={getExportData()}
          filename="usuarios"
          columns={exportColumns}
        />

        {hasSearched && (
          <Text className={styles.totalText}>
            {users.length} usuário{users.length !== 1 ? 's' : ''} carregado{users.length !== 1 ? 's' : ''}
          </Text>
        )}
      </div>

      {/* Empty state */}
      {!hasSearched && (
        <div className={styles.emptyState}>
          <Text>Faça uma pesquisa para visualizar os usuários</Text>
        </div>
      )}

      {hasSearched && users.length === 0 && !loading && (
        <div className={styles.emptyState}>
          <Text>Nenhum usuário encontrado</Text>
        </div>
      )}

      {/* TABLE AREA */}
      {hasSearched && users.length > 0 && (
        <>
          <div className={styles.tableContainer}>
            <Table className={styles.table} size="small" aria-label="Users table">
              <TableHeader className={styles.tableHeader}>
                <TableRow>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '42%' }}>
                    Nome
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '17%' }}>
                    ID Externo
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '7%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Turmas como Aluno" relationship="label">
                        <HatGraduation20Regular />
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '7%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Turmas como Professor" relationship="label">
                        <PersonBoard20Regular />
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '12%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Corrigir grupos do usuario (remove e re-adiciona)" relationship="label">
                        <span>Corrigir Grupos</span>
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '6%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      Detalhes
                    </div>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className={styles.tableRow}>
                    <TableCell style={{ width: '42%' }}>
                      {user.displayName}
                    </TableCell>
                    <TableCell style={{ width: '17%' }}>
                      {user.student?.externalId || user.teacher?.externalId || '-'}
                    </TableCell>
                    <TableCell style={{ width: '7%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderCountCell(user.id, 'studentClassCount')}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '7%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderCountCell(user.id, 'teacherClassCount')}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '12%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderFixGroupsCell(user)}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '6%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Tooltip content="Ver detalhes" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<Eye20Regular />}
                            size="small"
                            onClick={() => handleViewUser(user.id)}
                          />
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Loading more indicator */}
            {loadingMore && (
              <div className={styles.loadingMore}>
                <Spinner size="tiny" />
                <Text size={200}>Carregando mais...</Text>
              </div>
            )}
          </div>

          {/* PAGINATION AREA */}
          <div className={styles.paginationContainer}>
            <Text className={styles.paginationInfo}>
              Mostrando {startItem}-{endItem} usuários
            </Text>

            <div className={styles.paginationButtons}>
              {nextLink && (
                <Button
                  appearance="primary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      <UserDetails
        userId={selectedUserId}
        open={modalOpen}
        onClose={handleCloseModal}
      />

      {/* Backup Choice Dialog */}
      <Dialog
        open={backupDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setBackupDialogOpen(false);
            setBackupInfo(null);
          }
        }}
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>Backup encontrado</DialogTitle>
            <DialogContent>
              {backupInfo && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Text>
                    Existe um backup de{' '}
                    <strong>
                      {backupInfo.backup.backupTimestamp
                        ? new Date(backupInfo.backup.backupTimestamp).toLocaleString('pt-BR')
                        : 'data desconhecida'}
                    </strong>{' '}
                    com <strong>{backupInfo.backup.backupCount}</strong> grupos salvos para{' '}
                    <strong>{backupInfo.userDisplayName}</strong>.
                  </Text>
                  <Text>
                    Escolha como deseja prosseguir:
                  </Text>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    <li><strong>Pela tabela:</strong> Apenas adiciona o usuario aos grupos do backup (sem remover)</li>
                    <li><strong>Online:</strong> Processo completo (remove e re-adiciona todos os grupos atuais)</li>
                  </ul>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => handleBackupChoice(true)}>
                Corrigir pela tabela
              </Button>
              <Button appearance="primary" onClick={() => handleBackupChoice(false)}>
                Corrigir online
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default UsersList;
